import { decodeElevations, decodeLine } from '../core/codec';
import { dayNumber } from '../core/time';
import { KIND_KEYS, SOIL_KEYS } from './types';
import type {
  KindKey,
  MapLines,
  Municipality,
  PrefPoint,
  SiteDef,
  SiteInput,
  SoilKey,
  TerrainData,
} from './types';

/** 同梱データの形が合わないときの例外。ファイル名と理由を持つ。 */
export class DataError extends Error {
  constructor(file: string, reason: string) {
    super(`${file}: ${reason}`);
    this.name = 'DataError';
  }
}

type Rec = Record<string, unknown>;
const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);

function num(file: string, o: Rec, key: string, where: string): number {
  const v = o[key];
  if (typeof v !== 'number' || !Number.isFinite(v))
    throw new DataError(file, `${where} の ${key} が数でない`);
  return v;
}
function int(file: string, o: Rec, key: string, where: string, min: number): number {
  const v = num(file, o, key, where);
  if (!Number.isInteger(v) || v < min)
    throw new DataError(file, `${where} の ${key} は ${min} 以上の整数`);
  return v;
}
function str(file: string, o: Rec, key: string, where: string): string {
  const v = o[key];
  if (typeof v !== 'string' || v === '')
    throw new DataError(file, `${where} の ${key} が文字でない`);
  return v;
}

const SITES_FILE = 'sites.json';
const TERRAIN_FILE = 'terrain.json';

export function parseSiteDefs(raw: unknown, prefNames: ReadonlySet<string>): SiteDef[] {
  if (!Array.isArray(raw) || raw.length === 0)
    throw new DataError(SITES_FILE, '現場の配列でない、または空');
  const seen = new Set<number>();
  return raw.map((r: unknown, i) => {
    const where = `${i + 1} 件目`;
    if (!isRec(r)) throw new DataError(SITES_FILE, `${where} がオブジェクトでない`);
    const seq = int(SITES_FILE, r, 'seq', where, 1);
    if (seen.has(seq)) throw new DataError(SITES_FILE, `seq ${seq} が重複している`);
    seen.add(seq);
    const kind = str(SITES_FILE, r, 'kind', where);
    if (!(KIND_KEYS as readonly string[]).includes(kind))
      throw new DataError(SITES_FILE, `${where} の kind「${kind}」は未定義`);
    const soil = str(SITES_FILE, r, 'soil', where);
    if (!(SOIL_KEYS as readonly string[]).includes(soil))
      throw new DataError(SITES_FILE, `${where} の soil「${soil}」は未定義`);
    const pref = str(SITES_FILE, r, 'pref', where);
    if (!prefNames.has(pref))
      throw new DataError(SITES_FILE, `${where} の pref「${pref}」は都道府県にない`);
    const start = str(SITES_FILE, r, 'start', where);
    try {
      dayNumber(start);
    } catch {
      throw new DataError(SITES_FILE, `${where} の start「${start}」が日付でない`);
    }
    const w = num(SITES_FILE, r, 'w', where);
    const d = num(SITES_FILE, r, 'd', where);
    if (w <= 0 || d <= 0) throw new DataError(SITES_FILE, `${where} の施工範囲が正でない`);
    const lgcode = int(SITES_FILE, r, 'lgcode', where, 10000);
    if (lgcode > 999999) throw new DataError(SITES_FILE, `${where} の lgcode が 6 けたを超える`);
    return {
      seq,
      kind: kind as KindKey,
      name: str(SITES_FILE, r, 'name', where),
      client: str(SITES_FILE, r, 'client', where),
      start,
      perf: num(SITES_FILE, r, 'perf', where),
      soil: soil as SoilKey,
      pref,
      city: str(SITES_FILE, r, 'city', where),
      town: str(SITES_FILE, r, 'town', where),
      machiazaId: str(SITES_FILE, r, 'machiazaId', where),
      lgcode,
      lon: num(SITES_FILE, r, 'lon', where),
      lat: num(SITES_FILE, r, 'lat', where),
      w,
      d,
    };
  });
}

export function parseTerrain(raw: unknown, key: string): TerrainData {
  const where = `現場 ${key}`;
  if (!isRec(raw)) throw new DataError(TERRAIN_FILE, `${where} の標高がない`);
  const nx = int(TERRAIN_FILE, raw, 'nx', where, 1);
  const nz = int(TERRAIN_FILE, raw, 'nz', where, 1);
  const cn = int(TERRAIN_FILE, raw, 'cn', where, 1);
  const cw = num(TERRAIN_FILE, raw, 'cw', where);
  if (cw <= 0) throw new DataError(TERRAIN_FILE, `${where} の cw が正でない`);
  let E: Float32Array;
  let C: Float32Array;
  try {
    E = decodeElevations(str(TERRAIN_FILE, raw, 'E', where));
    C = decodeElevations(str(TERRAIN_FILE, raw, 'C', where));
  } catch (e) {
    if (e instanceof DataError) throw e;
    throw new DataError(TERRAIN_FILE, `${where} の標高を復元できない`);
  }
  if (E.length !== (nx + 1) * (nz + 1)) {
    throw new DataError(
      TERRAIN_FILE,
      `${where} の E は ${(nx + 1) * (nz + 1)} 点のはずが ${E.length} 点`,
    );
  }
  if (C.length !== (cn + 1) * (cn + 1)) {
    throw new DataError(
      TERRAIN_FILE,
      `${where} の C は ${(cn + 1) * (cn + 1)} 点のはずが ${C.length} 点`,
    );
  }
  return {
    nx,
    nz,
    src: str(TERRAIN_FILE, raw, 'src', where),
    E,
    cw,
    cn,
    C,
    emin: num(TERRAIN_FILE, raw, 'emin', where),
    emax: num(TERRAIN_FILE, raw, 'emax', where),
    emed: num(TERRAIN_FILE, raw, 'emed', where),
  };
}

/** 現場の定義と標高を検査して組にする。並びは `sites.json` の順。 */
export function parseSiteInputs(
  sitesRaw: unknown,
  terrainRaw: unknown,
  prefNames: ReadonlySet<string>,
): SiteInput[] {
  if (!isRec(terrainRaw)) throw new DataError(TERRAIN_FILE, '連番で引くオブジェクトでない');
  return parseSiteDefs(sitesRaw, prefNames).map((def) => ({
    def,
    terrain: parseTerrain(terrainRaw[String(def.seq)], String(def.seq)),
  }));
}

const PREFS_FILE = 'prefectures.json';
const CITIES_FILE = 'municipalities.json';

/** 都道府県の名前と代表地点を検査して取り出す。 */
export function parsePrefPoints(raw: unknown): PrefPoint[] {
  if (!Array.isArray(raw) || raw.length !== 47)
    throw new DataError(PREFS_FILE, '47 件の配列でない');
  return raw.map((p, i) => {
    const where = `${i} 件目`;
    if (!isRec(p)) throw new DataError(PREFS_FILE, `${where} がオブジェクトでない`);
    const pt = p.pt;
    if (
      !Array.isArray(pt) ||
      pt.length < 2 ||
      !pt.every((v) => typeof v === 'number' && Number.isFinite(v))
    )
      throw new DataError(PREFS_FILE, `${where} の pt が [経度, 緯度] でない`);
    return { name: str(PREFS_FILE, p, 'name', where), lon: pt[0] as number, lat: pt[1] as number };
  });
}

/** 市区町村の代表地点を検査して取り出す。形は [都道府県の添字, 名前, 経度, 緯度]。 */
export function parseMunicipalities(raw: unknown, prefs: readonly PrefPoint[]): Municipality[] {
  if (!Array.isArray(raw) || raw.length === 0)
    throw new DataError(CITIES_FILE, '空でない配列でない');
  return raw.map((c, i) => {
    const where = `${i} 件目`;
    if (!Array.isArray(c) || c.length !== 4)
      throw new DataError(CITIES_FILE, `${where} が 4 要素の配列でない`);
    const [pi, name, lon, lat] = c as unknown[];
    const pref = typeof pi === 'number' && Number.isInteger(pi) ? prefs[pi] : undefined;
    if (!pref) throw new DataError(CITIES_FILE, `${where} の都道府県の添字が範囲の外`);
    if (typeof name !== 'string' || name === '')
      throw new DataError(CITIES_FILE, `${where} の名前が文字でない`);
    if (
      typeof lon !== 'number' ||
      !Number.isFinite(lon) ||
      typeof lat !== 'number' ||
      !Number.isFinite(lat)
    )
      throw new DataError(CITIES_FILE, `${where} の経度・緯度が数でない`);
    return { pref: pref.name, name, lon, lat };
  });
}

const ROADS_FILE = 'expressways.json';

/** 県境の輪郭と高速道路の線を検査して復元する（CSSP-SPEC-001 §3.2）。 */
export function parseMapLines(prefsRaw: unknown, roadsRaw: unknown): MapLines {
  if (!Array.isArray(prefsRaw) || prefsRaw.length !== 47)
    throw new DataError(PREFS_FILE, '47 件の配列でない');
  const line = (file: string, a: unknown, where: string): [number, number][] => {
    if (!Array.isArray(a) || !a.every((v) => typeof v === 'number' && Number.isFinite(v)))
      throw new DataError(file, `${where} が数の配列でない`);
    try {
      return decodeLine(a as number[]);
    } catch {
      throw new DataError(file, `${where} の符号が不正`);
    }
  };
  const prefs = prefsRaw.map((p, i) => {
    const where = `${i} 件目`;
    if (!isRec(p) || !Array.isArray(p.rings) || p.rings.length === 0)
      throw new DataError(PREFS_FILE, `${where} の rings がない`);
    const rings = p.rings.map((r, k) => line(PREFS_FILE, r, `${where} の輪郭 ${k}`));
    const best = rings.reduce((a, b) => (b.length > a.length ? b : a));
    const main: [number, number, number, number] = [999, 999, -999, -999];
    for (const [x, y] of best) {
      main[0] = Math.min(main[0], x);
      main[1] = Math.min(main[1], y);
      main[2] = Math.max(main[2], x);
      main[3] = Math.max(main[3], y);
    }
    return { name: str(PREFS_FILE, p, 'name', where), rings, main };
  });
  if (!Array.isArray(roadsRaw) || roadsRaw.length === 0)
    throw new DataError(ROADS_FILE, '空でない配列でない');
  const roads = roadsRaw.map((r, i) => line(ROADS_FILE, r, `${i} 件目`));
  return { prefs, roads };
}
